import React, { useMemo } from 'react'
// @ts-ignore no types
import { connect } from 'react-redux'
import classnames from 'classnames/bind'
import * as graph from '@adc/sdk'
import { Button, Icon, Text, NOTIFICATION_ROLES, constants } from '@adc/ui-adc'
import type { primitives } from '@adc/blueprint'
import { useListingContext } from 'common/context/listing'
import { useModalFeatures } from 'common/hooks/useModalFeatures'
import { getRegistrationRestrictedValues } from '../../utils/listing'
import analytics from '../../services/analytics'
import { getApplicationNotificationsContext } from '../../utils/applicationNotificationsBridge'
import { handleRegistration } from '../auctionDetails/common'
// @ts-ignore no types
import styles from './index.scss'

const cx = classnames.bind(styles)

const CONFIRM_EMAIL_SEALED_BID_CONTENT = (
  <div>
    <p>
      Check your email to confirm your account. Then, log out of your account
      and log back in so we can confirm your status.
    </p>
    <p>This must be done in order to make offers.</p>
  </div>
)

export type SealedBidModalType =
  | 'counter-offer-modal'
  | 'update-offer-modal'
  | 'guided-offer-modal'
  | null

interface SealedBidCallToActionProps {
  user: primitives.AuctionUser
  elmId: string
}

export const SealedBidCallToActionFunctional = ({
  user,
  elmId,
}: SealedBidCallToActionProps) => {
  const { segment, listing } = useListingContext()

  const notifications = getApplicationNotificationsContext()

  const { toggleAuthModal, pushOnboardingQueueItem } = useModalFeatures()
  let accountRestrictedAlertValues: ReturnType<
    typeof getRegistrationRestrictedValues
  > | null = null

  if (!listing.is_current_user_registered_for_online_auction) {
    accountRestrictedAlertValues = getRegistrationRestrictedValues({
      user,
      listing,
    })
  }

  const currentUserBidRank = useMemo(
    () => segment.__methods__.getCurrentUserSealedBidRank(),
    [segment]
  )

  // TODO the following logic for offerWithDrawnAndInHighestAndBestOpenPeriod and disAllowInitialOffer should be inside
  // segment?.__methods__?.allowToSubmitNewOffers() confirm with the team
  // created ticket https://auction.atlassian.net/browse/SELL-281
  // @ts-ignore
  const offerWithDrawnAndInHighestAndBestOpenPeriod =
    segment?.current_user_bidding_status ===
      graph.enums.OnlineAuctionSegmentBiddingStatus.WithdrawnBid &&
    segment?.segment_status ===
      graph.enums.OnlineAuctionSegmentStatus.HighestAndBestOpen

  // no new offers allowed while in these statuses, TODO: should be replaced by BE flag
  const disAllowInitialOffer =
    [
      graph.enums.OnlineAuctionSegmentStatus.HighestAndBestDecision,
      graph.enums.OnlineAuctionSegmentStatus.HighestAndBestOpen,
    ].includes(segment?.segment_status) &&
    !offerWithDrawnAndInHighestAndBestOpenPeriod

  const handleMakeInitialOffer = () => {
    // @ts-ignore status exists on User type
    if (user.status === graph.enums.UserStatus.Confirmed) {
      window?.location?.assign?.(`${listing.listing_page_path}/offer`)
    } else {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call
      notifications.overlayBanner.open({
        message: CONFIRM_EMAIL_SEALED_BID_CONTENT,
        title: 'Confirm Your Email',
        role: NOTIFICATION_ROLES.ERROR,
        centered: false,
      })
    }
  }

  const renderButton = () => {
    const trackingFn =
      elmId === 'bid_box'
        ? analytics.privateSeller.makeAnOfferBidBoxButtonClick
        : analytics.privateSeller.makeAnOfferStickyHeaderButtonClick
    if (!listing.is_current_user_registered_for_online_auction) {
      return (
        <>
          <div className={cx('button-wrapper')}>
            <Button
              color={constants.ColorNames.Purple}
              size='large'
              role='primary'
              disabled={
                accountRestrictedAlertValues?.isRegistrationButtonDisabled ||
                !segment?.configuration.is_registration_enabled
              }
              elmId='make_an_offer_button'
              onClick={() => {
                trackingFn()
                return handleRegistration({
                  actions: {
                    toggleAuthModal,
                    pushOnboardingQueueItem,
                  },
                  listing,
                  user,
                  actionSource: 'make-an-offer',
                })()
              }}
            >
              Make an Offer
            </Button>
          </div>
        </>
      )
    } else if (currentUserBidRank) {
      return (
        <div className={cx('button-wrapper')}>
          <Button
            color={constants.ColorNames.Info}
            size='large'
            role='primary'
            elmId='go_to_offers_button'
            onClick={() => {
              handleMakeInitialOffer()
            }}
          >
            Go To My Offer{' '}
            <Icon name='arrow-right' iconStyle='solid' color='inherit' />
          </Button>
        </div>
      )
    } else {
      return (
        <div className={cx('button-wrapper')}>
          <Button
            color={constants.ColorNames.Purple}
            size='large'
            role='primary'
            disabled={disAllowInitialOffer}
            elmId='make_an_offer_button'
            onClick={() => {
              handleMakeInitialOffer()
              trackingFn()
            }}
          >
            Make an Offer
          </Button>
        </div>
      )
    }
  }

  return (
    <div className={cx('sealed-bid-cta-container')}>
      {renderButton()}
      <Button
        size='medium'
        role='tertiary'
        tag='a'
        href='https://www.auction.com/lp/buyer-offer-guide/'
        target='_BLANK'
        rel='noreferrer'
        data-elm-id={`${elmId}_learn_more_about_offers`}
      >
        <Text color='info'>Learn More about Offers</Text>
      </Button>
    </div>
  )
}

const mapStateToProps = (store: any) => {
  return {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    user: store.user,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    featureFlags: store.featureFlags,
  }
}

export const SealedBidCallToAction = connect(mapStateToProps)(
  SealedBidCallToActionFunctional
)
